/**
 * useAnalytics — hook minimal pour émettre des événements GA4 (gtag)
 * uniquement si l'utilisateur a accepté les cookies analytiques.
 *
 * Stratégie de consentement :
 * - Avant acceptation : aucun `gtag('event', ...)` ne part (no-op silencieux)
 * - Après acceptation : événements normalement envoyés
 *
 * Le tag GA4 lui-même est chargé dès le HTML (gtag base script) mais
 * configuré avec `consent default 'denied'` au démarrage, puis upgradé
 * via `gtag('consent', 'update')` après acceptation. C'est l'approche
 * recommandée par Google "Consent Mode v2" — conforme RGPD/nLPD France.
 */
import { useCallback } from "react";

const STORAGE_KEY = "signapharma_cookie_consent";

export function hasConsented() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "accepted";
  } catch {
    return false;
  }
}

export function setConsent(accepted) {
  try {
    localStorage.setItem(STORAGE_KEY, accepted ? "accepted" : "denied");
  } catch {
    /* private mode / quota */
  }
  // Met à jour Google Consent Mode v2
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: accepted ? "granted" : "denied",
      analytics_storage: accepted ? "granted" : "denied",
      ad_user_data: accepted ? "granted" : "denied",
      ad_personalization: accepted ? "granted" : "denied",
    });
  }
}

/**
 * Émet un événement GA4. Si l'utilisateur n'a pas consenti, l'événement
 * sera silencieusement ignoré par GA grâce au consent mode "denied".
 * On envoie quand même l'event pour permettre les conversions modélisées.
 */
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  try {
    window.gtag("event", name, params);
  } catch {
    /* ignore */
  }
}

export function useAnalytics() {
  const track = useCallback((name, params = {}) => trackEvent(name, params), []);
  return { track, hasConsented, setConsent };
}
